/**
 * The one deterministic RNG of the whole game (architecture §3.5; combat §4.8): **cyrb128** string hash → 4 × u32
 * state → **sfc32**. Streams are derived from the *root seed string*, not from the parent's current state, so adding a
 * random call in one subsystem never shifts another subsystem's numbers.
 *
 * Seeding map (architecture §3.5): combat `new Rng(spec.seed).stream('core' | 'ai' | 'omen' | 'gloss' | 'arena' | 'auto')`;
 * exploration `new Rng(save.seed).stream('explo', tickIndex, streamId)`; cards `${save.seed}:cards:${matchIndex}`;
 * economy `${save.seed}:econ:${source}:${counter}`; animation actors `${sceneSeed}:anim:${actorId}`; VFX
 * `hashKey(sceneSeed, 'vfx', eventIndex, burstIndex)`.
 *
 * Golden vectors (Acceptance test 3, `tests/shared/rng.test.ts`): `new Rng('42').nextU32()` ×3 = 3773934568,
 * 1728522555, 1708811191.
 *
 * No TS parameter properties / enums: the file stays runnable with `node --experimental-strip-types`.
 */

/** cyrb128 string hash → four u32 words (the sfc32 state). */
export function cyrb128(str: string): [number, number, number, number] {
  let h1 = 1779033703, h2 = 3144134277, h3 = 1013904242, h4 = 2773480762;
  for (let i = 0, k: number; i < str.length; i++) {
    k = str.charCodeAt(i);
    h1 = h2 ^ Math.imul(h1 ^ k, 597399067);
    h2 = h3 ^ Math.imul(h2 ^ k, 2869860233);
    h3 = h4 ^ Math.imul(h3 ^ k, 951274213);
    h4 = h1 ^ Math.imul(h4 ^ k, 2716044179);
  }
  h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067);
  h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233);
  h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213);
  h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179);
  h1 ^= h2 ^ h3 ^ h4; h2 ^= h1; h3 ^= h1; h4 ^= h1;
  return [h1 >>> 0, h2 >>> 0, h3 >>> 0, h4 >>> 0];
}

/** A seeded sfc32 generator. Same key ⇒ same sequence, in Node and in every browser. */
export class Rng {
  /** The full seed string of this stream, e.g. `'42:core'`. */
  readonly key: string;
  private a: number; private b: number; private c: number; private d: number;
  constructor(key: string) {                    // no TS parameter properties: keeps the file runnable with node --experimental-strip-types
    this.key = key;
    [this.a, this.b, this.c, this.d] = cyrb128(key);
  }
  /** Child stream: key = `${this.key}:${parts.join(':')}` — e.g. new Rng('42').stream('core') has key '42:core'. */
  stream(...parts: Array<string | number>): Rng { return new Rng(`${this.key}:${parts.join(':')}`); }
  /** uint32 */
  nextU32(): number {
    const t = (((this.a + this.b) | 0) + this.d) | 0;
    this.d = (this.d + 1) | 0;
    this.a = this.b ^ (this.b >>> 9);
    this.b = (this.c + (this.c << 3)) | 0;
    this.c = (this.c << 21) | (this.c >>> 11);
    this.c = (this.c + t) | 0;
    return t >>> 0;
  }
  /** Uniform float in [0, 1). */
  float(): number { return this.nextU32() / 4294967296; }                 // [0, 1)
  /** Uniform integer in [min, maxIncl]. */
  int(min: number, maxIncl: number): number { return min + Math.floor(this.float() * (maxIncl - min + 1)); }
  /** True with probability p. */
  chance(p: number): boolean { return this.float() < p; }
  /** A uniformly picked element; `xs` must be non-empty. */
  pick<T>(xs: readonly T[]): T { return xs[Math.floor(this.float() * xs.length)]; }   // xs non-empty
  /** Weights ≤ 0 are skipped; xs must contain at least one positive weight. */
  weighted<T>(xs: readonly { w: number; v: T }[]): T {
    let total = 0;
    for (const x of xs) total += Math.max(0, x.w);
    let r = this.float() * total;
    for (const x of xs) {
      const w = Math.max(0, x.w);
      if (r < w) return x.v;
      r -= w;
    }
    return xs[xs.length - 1].v;
  }
  /** In-place Fisher–Yates from the end. */
  shuffle<T>(xs: T[]): T[] {
    for (let i = xs.length - 1; i > 0; i--) {
      const j = this.int(0, i);
      const tmp = xs[i]; xs[i] = xs[j]; xs[j] = tmp;
    }
    return xs;
  }
  /** The 4 × u32 state, for save/replay (`Rng.restore(key, getState())` continues the identical sequence). */
  getState(): [number, number, number, number] { return [this.a >>> 0, this.b >>> 0, this.c >>> 0, this.d >>> 0]; }
  /** Rebuilds a generator mid-sequence from `getState()`. */
  static restore(key: string, st: [number, number, number, number]): Rng {
    const r = new Rng(key);
    r.a = st[0] | 0; r.b = st[1] | 0; r.c = st[2] | 0; r.d = st[3] | 0;
    return r;
  }
}
/** First cyrb128 word of the joined parts — e.g. dungeon layout seeds, VFX burst seeds. */
export const hashKey = (...parts: Array<string | number>): number => cyrb128(parts.join(':'))[0]; // e.g. dungeon layout seeds
